import MobileIcon from 'components/assets/smartphone.svg';
import AndroidIcon from 'components/assets/android.svg';
import IosIcon from 'components/assets/apple.svg';
import Txt from 'components/atoms/Txt/Txt';
import React from 'react';

export type OfferType = 'cashback' | 'deal' | 'gaming';

interface MobileConfig {
  text: string;
  Icon: React.FunctionComponent<{ className?: string }>;
  alt: string;
  // shows the mobile notice when !device[deviceKey]
  deviceKey: 'isAndroid' | 'isIOS' | 'isMobile';
  storeFilter: 'android' | 'ios' | undefined;
  hideStoreLinks?: boolean;
}

export const MOBILE_CONFIG: Record<string, MobileConfig> = {
  android_only: {
    text: 'Android only',
    Icon: AndroidIcon,
    alt: 'An android phone logo',
    deviceKey: 'isAndroid',
    storeFilter: 'android',
  },
  ios_only: {
    text: 'iOS only',
    Icon: IosIcon,
    alt: 'An Apple iOS logo',
    deviceKey: 'isIOS',
    storeFilter: 'ios',
    hideStoreLinks: true,
  },
  mobile_only: {
    text: 'Mobile only',
    Icon: MobileIcon,
    alt: 'An icon depicting a smartphone',
    deviceKey: 'isMobile',
    storeFilter: undefined,
  },
};

export function mobileConfigFromTags(
  tags: readonly string[],
): typeof MOBILE_CONFIG.android_only | undefined {
  const mobile = tags.find((tag) => ['ios_only', 'android_only', 'mobile_only'].includes(tag)) as
    | 'ios_only'
    | 'android_only'
    | 'mobile_only'
    | undefined;
  return mobile ? MOBILE_CONFIG[mobile] : undefined;
}

export const PROMOTION_TAG = 'promo';

const OFFER_TYPE_LABELS: Record<OfferType, string> = {
  cashback: 'cashback offers',
  deal: 'deals',
  gaming: 'gaming offers',
};

export function getNoOffersMessage(
  offerType: OfferType,
  categoryName?: string,
  searchQuery?: string,
) {
  const offerLabel = OFFER_TYPE_LABELS[offerType];

  const categoryPart = categoryName && (
    <>
      {' '}
      for <strong>{categoryName}</strong>
    </>
  );

  const searchPart = searchQuery && (
    <>
      {' '}
      matching &quot;<strong>{searchQuery}</strong>&quot;
    </>
  );

  return {
    messageHighlight: 'hmmm!',
    message: (
      <>
        <Txt component="p">
          We don&apos;t have any {offerLabel}
          {categoryPart}
          {searchPart} available right now, but we&apos;re constantly searching for the best{' '}
          {offerLabel} to help you earn &amp; save money!
        </Txt>
        <Txt component="p">
          We update Qmee with the best {offerLabel} all the time, so check back soon!
        </Txt>
      </>
    ),
    messageClassName: 'noOffersMessage',
  };
}

export const OFFER_NOT_AVAILABLE_MESSAGE = {
  messageHighlight: 'sorry!',
  message: 'It looks like this offer is no longer available.',
};

export const OFFER_NOT_AVAILABLE_IN_COUNTRY_MESSAGE = {
  messageHighlight: 'sorry!',
  message: 'It looks like this offer is not available in your country.',
};

export type CategoryDetector = (node: {
  readonly tags: ReadonlyArray<string>;
  readonly categories: ReadonlyArray<{ readonly tag: string }>;
}) => string;

export const detectGamingPlatform: CategoryDetector = (node) => {
  const tags = node.tags as string[];
  const hasAndroid = tags.some((tag) => tag.toLowerCase().includes('android'));
  const hasIOS = tags.some((tag) => tag.toLowerCase().includes('ios'));

  if (hasAndroid) return 'android';
  if (hasIOS) return 'ios';
  return 'allCategories';
};

export const TITLE_SUFFIX_BY_TYPE: Record<OfferType, string> = {
  cashback: 'Cashback',
  deal: '',
  gaming: '!',
};
export interface CategorizableItem {
  category: string;
}

export function buildCategories(
  baseCategories: { tag: string; name: string }[],
  items: CategorizableItem[],
): { tag: string; name: string; count: number }[] {
  const countMap = new Map<string, number>();
  baseCategories.forEach((cat) => countMap.set(cat.tag, 0));
  items.forEach((item) => {
    if (item.category && countMap.has(item.category)) {
      countMap.set(item.category, countMap.get(item.category)! + 1);
    }
  });
  return [
    { tag: 'allCategories', name: 'All Categories', count: items.length },
    ...baseCategories.map((cat) => ({
      tag: cat.tag,
      name: cat.name,
      count: countMap.get(cat.tag) ?? 0,
    })),
  ];
}

export function filterBySearch<
  T extends {
    name?: string | null;
    title: string;
    description?: string | null;
    tags?: readonly string[];
  },
>(items: T[], query: string): T[] {
  if (!query || query.length < 3) return items;
  const q = query.toLowerCase();
  return items.filter(
    (item) =>
      item.name?.toLowerCase().includes(q) ||
      item.title.toLowerCase().includes(q) ||
      item.description?.toLowerCase().includes(q) ||
      item.tags?.some((tag) => tag.toLowerCase().includes(q)),
  );
}

export function extractExtraContent(
  offerType: OfferType,
  description: string,
): {
  description: string;
  extraContent: string | null;
} {
  if (offerType !== 'gaming') {
    return { description, extraContent: null };
  }
  // If description is HTML, extract the first <p> content and return rest as extra HTML.
  // Otherwise (if plaintext), split on blank lines (paragraphs).
  if (/<p[\s>]/i.test(description)) {
    const pRegex = /<p[^>]*>([\s\S]*?)<\/p>/i;
    const pMatch = pRegex.exec(description);
    if (!pMatch) {
      return { description, extraContent: null };
    }

    const firstPContent = pMatch[1];
    const firstPEndIndex = (pMatch.index ?? 0) + pMatch[0].length;
    const remainingHTML = description.slice(firstPEndIndex).trim();
    return {
      description: firstPContent,
      extraContent: remainingHTML || null,
    };
  }

  // Markdown / plain text: split into paragraphs by blank lines
  const parts = description.split(/\r?\n\s*\r?\n/);
  const first = parts[0] ?? '';
  const rest = parts.slice(1).filter(Boolean);
  return {
    description: first,
    extraContent: rest.length > 0 ? rest.join('\n\n') : null,
  };
}

const PLATFORM_TAG_ALIASES: Record<string, string> = {
  android_only: 'android',
  ios_only: 'ios',
  mobile_only: 'mobile',
  desktop_only: 'desktop',
};

function normalizePromoTags(tags?: readonly string[] | null): Set<string> {
  const result = new Set<string>();
  for (const raw of tags ?? []) {
    const tag = raw.toLowerCase().trim();
    // drop promo tag
    if (!tag || tag === PROMOTION_TAG) continue;
    // normalize platform tags
    const alias = PLATFORM_TAG_ALIASES[tag];
    if (alias) {
      result.add(alias);
      continue;
    }
    // strip otherwise
    const stripped = tag.replace(/[_-]?(only|app|exclusive)$/, '');
    if (stripped) result.add(stripped);
  }
  return result;
}

type Promo = {
  tags?: readonly string[] | null;
  categories?: ReadonlyArray<{ readonly tag: string }> | null;
};

export function promoMatchesCategory(
  promo: Promo,
  selectedCategory: string,
  baseCategories: { tag: string }[],
): boolean {
  const rawTags = (promo.tags as string[] | undefined) ?? [];
  if (!rawTags.includes(PROMOTION_TAG)) return false;

  const categoryTags: string[] =
    promo.categories && promo.categories.length > 0
      ? promo.categories.map((c) => c.tag).filter(Boolean)
      : Array.from(normalizePromoTags(rawTags));

  if (selectedCategory === 'allCategories') {
    return baseCategories.some((cat) => categoryTags.includes(cat.tag));
  }
  return categoryTags.includes(selectedCategory);
}
